export type HorizontalRailPointerDrag = {
    rail: HTMLElement;
    pointerId: number;
    startClientX: number;
    startClientY: number;
    startScrollLeft: number;
    hasDragged: boolean;
    lastTimestamp?: number;
    velocityPixelsPerMillisecond: number;
};

export type HorizontalRailMomentum = {
    rail: HTMLElement;
    velocityPixelsPerMillisecond: number;
};

export type HorizontalRailPointerDragEnd = {
    shouldSuppressClick: boolean;
    momentum?: HorizontalRailMomentum;
};

type HorizontalRailPointerDown = Pick<PointerEvent, "button" | "clientX" | "clientY" | "isPrimary" | "pointerId" | "pointerType"> &
    Partial<Pick<PointerEvent, "timeStamp">>;

type HorizontalRailPointerMove = Pick<PointerEvent, "clientX" | "clientY" | "pointerId" | "preventDefault" | "timeStamp">;

export type HorizontalRailScrollerOptions = {
    root: HTMLElement;
    railSelector: string;
    scrollLeftDataAttribute?: string;
    restoreScrollPosition?: boolean;
    onDragStart?: (drag: HorizontalRailPointerDrag) => void;
    onDragEnd?: (drag: HorizontalRailPointerDrag) => void;
};

const HorizontalRailDragThresholdPixels = 6;
const HorizontalRailMomentumFrictionPerFrame = 0.92;
const HorizontalRailMomentumMinimumVelocity = 0.03;
const HorizontalRailMomentumMaximumVelocity = 2.2;
const DefaultScrollLeftDataAttribute = "smartguideRailScrollLeft";

export class HorizontalRailScroller {
    private pointerDrag?: HorizontalRailPointerDrag;
    private momentum?: HorizontalRailMomentum;
    private momentumAnimationFrame?: number;
    private momentumTimestamp?: number;
    private restoreAnimationFrame?: number;
    private suppressNextClick = false;

    constructor(private readonly options: HorizontalRailScrollerOptions) {
    }

    connect(): void {
        this.options.root.addEventListener("pointerdown", this.beginPointerDrag);
        this.options.root.addEventListener("pointermove", this.updatePointerDrag);
        this.options.root.addEventListener("pointerup", this.endPointerDrag);
        this.options.root.addEventListener("pointercancel", this.cancelPointerDrag);
        this.options.root.addEventListener("dragstart", this.preventNativeDrag);
        this.options.root.addEventListener("wheel", this.scrollOnWheel, { passive: false });
        this.options.root.addEventListener("click", this.suppressClickAfterDrag, true);
        document.addEventListener("turbo:before-cache", this.persistScrollPositions);
        if (this.options.restoreScrollPosition !== false) this.restoreScrollPositions();
        if (this.options.restoreScrollPosition !== false && typeof requestAnimationFrame === "function") {
            this.restoreAnimationFrame = requestAnimationFrame(this.restoreScrollPositions);
        }
    }

    disconnect(): void {
        this.options.root.removeEventListener("pointerdown", this.beginPointerDrag);
        this.options.root.removeEventListener("pointermove", this.updatePointerDrag);
        this.options.root.removeEventListener("pointerup", this.endPointerDrag);
        this.options.root.removeEventListener("pointercancel", this.cancelPointerDrag);
        this.options.root.removeEventListener("dragstart", this.preventNativeDrag);
        this.options.root.removeEventListener("wheel", this.scrollOnWheel);
        this.options.root.removeEventListener("click", this.suppressClickAfterDrag, true);
        document.removeEventListener("turbo:before-cache", this.persistScrollPositions);
        if (this.restoreAnimationFrame !== undefined && typeof cancelAnimationFrame === "function") {
            cancelAnimationFrame(this.restoreAnimationFrame);
            this.restoreAnimationFrame = undefined;
        }

        this.cancelMomentum();
        endHorizontalRailPointerDrag(this.pointerDrag);
        this.pointerDrag = undefined;
        this.suppressNextClick = false;
    }

    stopMomentum(): void {
        this.cancelMomentum();
    }

    private readonly beginPointerDrag = (event: Event): void => {
        if (!(event instanceof PointerEvent) || !(event.target instanceof Element)) {
            return;
        }

        const rail = this.findRail(event.target);
        if (!rail) {
            return;
        }

        const drag = beginHorizontalRailPointerDrag(rail, event);
        if (!drag) {
            return;
        }

        this.cancelMomentum();
        this.pointerDrag = drag;
        this.options.onDragStart?.(drag);
    };

    private readonly updatePointerDrag = (event: Event): void => {
        if (!(event instanceof PointerEvent)) {
            return;
        }

        const wasDragging = this.pointerDrag?.hasDragged ?? false;
        updateHorizontalRailPointerDrag(this.pointerDrag, event);
        if (!wasDragging && this.pointerDrag?.hasDragged) {
            this.pointerDrag.rail.setPointerCapture?.(event.pointerId);
        }
    };

    private readonly endPointerDrag = (event: Event): void => {
        if (!(event instanceof PointerEvent)) {
            return;
        }

        if (!this.pointerDrag || event.pointerId !== this.pointerDrag.pointerId) {
            return;
        }

        const drag = this.pointerDrag;
        const rail = drag.rail;
        const result = endHorizontalRailPointerDrag(this.pointerDrag);
        this.suppressNextClick = result.shouldSuppressClick;
        this.pointerDrag = undefined;
        if (rail.hasPointerCapture?.(event.pointerId)) {
            rail.releasePointerCapture?.(event.pointerId);
        }
        if (this.options.onDragEnd) this.options.onDragEnd(drag);
        else this.startMomentum(result.momentum);
    };

    private readonly cancelPointerDrag = (): void => {
        const drag = this.pointerDrag;
        endHorizontalRailPointerDrag(this.pointerDrag);
        this.pointerDrag = undefined;
        this.suppressNextClick = false;
        if (drag) this.options.onDragEnd?.(drag);
    };

    private readonly suppressClickAfterDrag = (event: Event): void => {
        if (!this.suppressNextClick) {
            return;
        }

        this.suppressNextClick = false;
        if (!(event.target instanceof Element) || !this.findRail(event.target)) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        if ("stopImmediatePropagation" in event) {
            event.stopImmediatePropagation();
        }
    };

    private readonly preventNativeDrag = (event: Event): void => {
        if (event instanceof DragEvent &&
            event.target instanceof Element &&
            this.findRail(event.target)) {
            event.preventDefault();
        }
    };

    private readonly scrollOnWheel = (event: Event): void => {
        if (!(event instanceof WheelEvent) || !(event.target instanceof Element)) {
            return;
        }

        const rail = this.findRail(event.target);
        if (!rail) {
            return;
        }

        this.cancelMomentum();
        if (scrollHorizontalRailOnWheel(rail, event)) {
            return;
        }

        if (event.deltaY !== 0 && !event.shiftKey && Math.abs(event.deltaY) >= Math.abs(event.deltaX)) {
            event.preventDefault();
            window.scrollBy({ top: event.deltaY, left: 0, behavior: "auto" });
        }
    };

    private readonly persistScrollPositions = (): void => {
        for (const rail of this.railElements()) {
            rail.dataset[this.scrollLeftDataAttribute] = rail.scrollLeft.toString();
        }
    };

    private readonly restoreScrollPositions = (): void => {
        this.restoreAnimationFrame = undefined;

        for (const rail of this.railElements()) {
            const savedScrollLeft = Number.parseFloat(rail.dataset[this.scrollLeftDataAttribute] ?? "");
            if (!Number.isFinite(savedScrollLeft)) {
                continue;
            }

            rail.scrollLeft = Math.max(0, savedScrollLeft);
        }
    };

    private findRail(target: Element): HTMLElement | null {
        const rail = target.closest<HTMLElement>(this.options.railSelector);
        return rail && this.options.root.contains(rail)
            ? rail
            : null;
    }

    private railElements(): HTMLElement[] {
        return Array.from(this.options.root.querySelectorAll<HTMLElement>(this.options.railSelector));
    }

    private startMomentum(momentum: HorizontalRailMomentum | undefined): void {
        this.cancelMomentum();
        if (!momentum || typeof requestAnimationFrame !== "function") {
            return;
        }

        this.momentum = momentum;
        this.momentumTimestamp = undefined;
        const animate = (timestamp: number): void => {
            if (!this.momentum) {
                return;
            }

            const previousTimestamp = this.momentumTimestamp ?? timestamp - 16;
            const elapsedMilliseconds = Math.min(32, Math.max(1, timestamp - previousTimestamp));
            this.momentumTimestamp = timestamp;

            if (stepHorizontalRailMomentum(this.momentum, elapsedMilliseconds)) {
                this.momentumAnimationFrame = requestAnimationFrame(animate);
                return;
            }

            this.momentum = undefined;
            this.momentumAnimationFrame = undefined;
            this.momentumTimestamp = undefined;
        };

        this.momentumAnimationFrame = requestAnimationFrame(animate);
    }

    private cancelMomentum(): void {
        if (this.momentumAnimationFrame !== undefined && typeof cancelAnimationFrame === "function") {
            cancelAnimationFrame(this.momentumAnimationFrame);
        }

        this.momentum = undefined;
        this.momentumAnimationFrame = undefined;
        this.momentumTimestamp = undefined;
    }

    private get scrollLeftDataAttribute(): string {
        return this.options.scrollLeftDataAttribute ?? DefaultScrollLeftDataAttribute;
    }
}

export function beginHorizontalRailPointerDrag(
    rail: HTMLElement,
    event: HorizontalRailPointerDown
): HorizontalRailPointerDrag | undefined {
    if (event.button !== 0 ||
        !event.isPrimary ||
        event.pointerType !== "mouse" ||
        rail.scrollWidth <= rail.clientWidth) {
        return undefined;
    }

    return {
        rail,
        pointerId: event.pointerId,
        startClientX: event.clientX,
        startClientY: event.clientY,
        startScrollLeft: rail.scrollLeft,
        hasDragged: false,
        lastTimestamp: event.timeStamp,
        velocityPixelsPerMillisecond: 0
    };
}

export function updateHorizontalRailPointerDrag(
    drag: HorizontalRailPointerDrag | undefined,
    event: HorizontalRailPointerMove
): boolean {
    if (!drag || event.pointerId !== drag.pointerId) {
        return false;
    }

    const deltaX = event.clientX - drag.startClientX;
    const deltaY = event.clientY - drag.startClientY;
    if (!drag.hasDragged) {
        if (Math.abs(deltaX) < HorizontalRailDragThresholdPixels || Math.abs(deltaX) <= Math.abs(deltaY)) {
            return false;
        }

        drag.hasDragged = true;
        drag.rail.classList.add("destination-horizontal-rail--dragging");
    }

    const maxScrollLeft = drag.rail.scrollWidth - drag.rail.clientWidth;
    const previousScrollLeft = drag.rail.scrollLeft;
    drag.rail.scrollLeft = Math.max(0, Math.min(maxScrollLeft, drag.startScrollLeft - deltaX));
    const elapsedMilliseconds = Math.max(1, event.timeStamp - (drag.lastTimestamp ?? event.timeStamp - 16));
    drag.velocityPixelsPerMillisecond = _clamp(
        (drag.rail.scrollLeft - previousScrollLeft) / elapsedMilliseconds,
        -HorizontalRailMomentumMaximumVelocity,
        HorizontalRailMomentumMaximumVelocity
    );
    drag.lastTimestamp = event.timeStamp;
    event.preventDefault();
    return drag.rail.scrollLeft !== previousScrollLeft;
}

export function endHorizontalRailPointerDrag(drag: HorizontalRailPointerDrag | undefined): HorizontalRailPointerDragEnd {
    if (!drag) {
        return { shouldSuppressClick: false };
    }

    drag.rail.classList.remove("destination-horizontal-rail--dragging");
    return {
        shouldSuppressClick: drag.hasDragged,
        momentum: drag.hasDragged && Math.abs(drag.velocityPixelsPerMillisecond) >= HorizontalRailMomentumMinimumVelocity
            ? {
                rail: drag.rail,
                velocityPixelsPerMillisecond: drag.velocityPixelsPerMillisecond
            }
            : undefined
    };
}

export function stepHorizontalRailMomentum(momentum: HorizontalRailMomentum, elapsedMilliseconds: number): boolean {
    const maxScrollLeft = momentum.rail.scrollWidth - momentum.rail.clientWidth;
    if (maxScrollLeft <= 0) {
        return false;
    }

    const previousScrollLeft = momentum.rail.scrollLeft;
    const nextScrollLeft = _clamp(
        previousScrollLeft + momentum.velocityPixelsPerMillisecond * elapsedMilliseconds,
        0,
        maxScrollLeft
    );
    momentum.rail.scrollLeft = nextScrollLeft;
    if (nextScrollLeft === previousScrollLeft || nextScrollLeft === 0 || nextScrollLeft === maxScrollLeft) {
        return false;
    }

    momentum.velocityPixelsPerMillisecond *= Math.pow(
        HorizontalRailMomentumFrictionPerFrame,
        elapsedMilliseconds / 16
    );
    return Math.abs(momentum.velocityPixelsPerMillisecond) >= HorizontalRailMomentumMinimumVelocity;
}

export function scrollHorizontalRailOnWheel(rail: HTMLElement, event: WheelEvent): boolean {
    if (event.defaultPrevented) {
        return false;
    }

    const delta = _horizontalWheelDelta(event);
    if (delta === 0) {
        return false;
    }

    const maxScrollLeft = rail.scrollWidth - rail.clientWidth;
    if (maxScrollLeft <= 0) {
        return false;
    }

    const currentScrollLeft = rail.scrollLeft;
    const nextScrollLeft = _clamp(currentScrollLeft + delta, 0, maxScrollLeft);
    if (nextScrollLeft === currentScrollLeft) {
        return false;
    }

    event.preventDefault();
    rail.scrollLeft = nextScrollLeft;
    return true;
}

function _horizontalWheelDelta(event: WheelEvent): number {
    if (Math.abs(event.deltaX) >= Math.abs(event.deltaY) && event.deltaX !== 0) {
        return event.deltaX;
    }

    if (event.shiftKey && event.deltaY !== 0) {
        return event.deltaY;
    }

    return 0;
}

function _clamp(value: number, min: number, max: number): number {
    return Math.max(min, Math.min(max, value));
}
